import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../services/auth/authService';
import { DashboardHeader } from '../../components/layout/DashboardHeader/DashboardHeader';
import { DashboardSidebar } from '../../components/layout/DashboardSidebar/DashboardSidebar';
import { createSidebarItems } from '../../components/layout/DashboardSidebar/sidebarItems';
import { patientsService, Patient } from '../../services/patients/patientsService';
import { formatDate } from '../../utils/dateUtils';
import { getInitials } from '../../utils/userUtils';
import { CalendarDatePicker } from '../../components/CalendarDatePicker/CalendarDatePicker';
import { Users, UserCheck, TrendingUp, Check } from 'lucide-react';
import './PatientsPage.css';

const STORAGE_KEY_DATE_RANGE = 'patientsPage_insightDateRange';
const STORAGE_KEY_SHOW_ALL = 'patientsPage_showAllPatients';
const STORAGE_KEY_NAME_FILTER = 'patientsPage_nameFilter';

const getDefaultDateRange = (): { from: string; to: string } => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { from: fmt(from), to: fmt(to) };
};

const loadStoredDateRange = (): { from: string; to: string } => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_DATE_RANGE);
    if (!raw) return getDefaultDateRange();
    const parsed = JSON.parse(raw) as { from?: string; to?: string };
    const from = parsed?.from && /^\d{4}-\d{2}-\d{2}$/.test(parsed.from) ? parsed.from : null;
    const to = parsed?.to && /^\d{4}-\d{2}-\d{2}$/.test(parsed.to) ? parsed.to : null;
    if (from && to) return { from, to };
  } catch {
    // ignore
  }
  return getDefaultDateRange();
};

const loadStoredShowAll = (): boolean => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SHOW_ALL);
    return raw === 'true';
  } catch {
    return false;
  }
};

const loadStoredNameFilter = (): string => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_NAME_FILTER);
    return typeof raw === 'string' ? raw : '';
  } catch {
    return '';
  }
};

const PatientsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nameFilter, setNameFilter] = useState(loadStoredNameFilter);
  const [insightDateRange, setInsightDateRange] = useState<{ from: string; to: string }>(loadStoredDateRange);
  const [insights, setInsights] = useState({ total: 0, active: 0, new: 0 });
  const [showAllPatients, setShowAllPatients] = useState(loadStoredShowAll);

  const loadPatients = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await patientsService.getPatients();
      setPatients(data);
      setFilteredPatients(data);
    } catch (err: any) {
      console.error('Failed to load patients:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const calculateInsights = useCallback((patientsData: Patient[], from: string, to: string, showAll: boolean) => {
    if (showAll) {
      const total = patientsData.length;
      const active = patientsData.filter((p) => !!p.lastAppointmentDate).length;
      setInsights({ total, active, new: 0 });
      return;
    }

    const fromDate = new Date(from + 'T00:00:00');
    const toDate = new Date(to + 'T23:59:59');

    const dateInRange = (dateStr: string | undefined) => {
      if (!dateStr) return false;
      const d = new Date(dateStr);
      return d >= fromDate && d <= toDate;
    };

    const total = patientsData.filter((patient) => {
      const first = patient.firstAppointmentDate ? new Date(patient.firstAppointmentDate) : null;
      const last = patient.lastAppointmentDate ? new Date(patient.lastAppointmentDate) : null;
      if (!first && !last) return false;
      const rangeStart = first || last!;
      const rangeEnd = last || first!;
      return rangeStart <= toDate && rangeEnd >= fromDate;
    }).length;

    const active = patientsData.filter((patient) => dateInRange(patient.lastAppointmentDate)).length;
    const newInRange = patientsData.filter((patient) => dateInRange(patient.firstAppointmentDate)).length;

    setInsights({ total, active, new: newInRange });
  }, []);

  useEffect(() => {
    loadPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (patients.length >= 0 && insightDateRange.from && insightDateRange.to) {
      calculateInsights(patients, insightDateRange.from, insightDateRange.to, showAllPatients);
    }
  }, [patients, insightDateRange, showAllPatients, calculateInsights]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_DATE_RANGE, JSON.stringify(insightDateRange));
    } catch {
      // ignore
    }
  }, [insightDateRange]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_SHOW_ALL, showAllPatients ? 'true' : 'false');
    } catch {
      // ignore
    }
  }, [showAllPatients]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_NAME_FILTER, nameFilter);
    } catch {
      // ignore
    }
  }, [nameFilter]);

  const applyFilters = useCallback(() => {
    let filtered = [...patients];

    // Filter by name
    if (nameFilter.trim()) {
      const searchLower = nameFilter.toLowerCase();
      filtered = filtered.filter(
        (patient) =>
          patient.name.toLowerCase().includes(searchLower) ||
          patient.email?.toLowerCase().includes(searchLower)
      );
    }

    // Filter by insight date range (FROM/TO) unless "Show all patients" is on
    if (!showAllPatients) {
      const fromDate = new Date(insightDateRange.from + 'T00:00:00');
      const toDate = new Date(insightDateRange.to + 'T23:59:59');
      filtered = filtered.filter((patient) => {
        const first = patient.firstAppointmentDate ? new Date(patient.firstAppointmentDate) : null;
        const last = patient.lastAppointmentDate ? new Date(patient.lastAppointmentDate) : null;
        if (!first && !last) return false;
        const rangeStart = first || last!;
        const rangeEnd = last || first!;
        return rangeStart <= toDate && rangeEnd >= fromDate;
      });
    }

    setFilteredPatients(filtered);
  }, [patients, nameFilter, showAllPatients, insightDateRange.from, insightDateRange.to]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleLogout = () => {
    authService.clearTokens();
    navigate('/login');
  };

  const handleGoToProfile = () => {
    navigate('/profile');
  };


  const sidebarItems = createSidebarItems(navigate, location.pathname);

  return (
    <div className="dashboard">
      <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />

      <div className="dashboard-layout">
        <DashboardSidebar items={sidebarItems} />

        <div className="dashboard-container">
          <main className="dashboard-main-content">
            {loading && (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                Loading patients...
              </div>
            )}

            {error && (
              <div style={{ padding: '20px', background: '#fee2e2', borderRadius: '8px', color: '#b91c1c', marginBottom: '24px' }}>
                Error: {error}
              </div>
            )}

            {!loading && !error && (
              <div className="patients-page-wrap">
                <div className="patients-one-card">
                  {/* Patient Insights */}
                  <section className="patients-insights-section">
                    <div className="patients-insights-header">
                      <h2 className="patients-card-title">Patient Insights</h2>
                      <div className="patients-insights-date-and-option">
                        <div className="patients-date-range-box">
                          <div className="patients-date-range-from">
                            <CalendarDatePicker
                              label="FROM"
                              id="insight-from"
                              value={insightDateRange.from}
                              onChange={(from) => setInsightDateRange((prev) => ({
                                ...prev,
                                from: from || prev.to,
                              }))}
                              maxDate={insightDateRange.to}
                              placeholder="Select date"
                              dateDisplayFormat="short"
                            />
                          </div>
                          <div className="patients-date-range-divider" aria-hidden />
                          <div className="patients-date-range-to">
                            <CalendarDatePicker
                              label="TO"
                              id="insight-to"
                              value={insightDateRange.to}
                              onChange={(to) => setInsightDateRange((prev) => ({
                                ...prev,
                                to: to || prev.from,
                              }))}
                              minDate={insightDateRange.from}
                              placeholder="Select date"
                              dateDisplayFormat="short"
                            />
                          </div>
                        </div>
                        <label className="show-all-patients-label">
                          <input
                            type="checkbox"
                            checked={showAllPatients}
                            onChange={(e) => setShowAllPatients(e.target.checked)}
                            className="show-all-patients-checkbox"
                            aria-label="Show all patients regardless of date range"
                          />
                          <span>Show all patients</span>
                        </label>
                      </div>
                    </div>
                    <div className="patients-insights">
                      <div className="insight-card">
                        <div className="insight-icon insight-icon-total">
                          <Users size={20} strokeWidth={2} />
                        </div>
                        <div className="insight-content">
                          <div className="insight-label">Total Patients</div>
                          <div className="insight-value">{insights.total}</div>
                        </div>
                      </div>
                      <div className="insight-card">
                        <div className="insight-icon insight-icon-active">
                          <UserCheck size={20} strokeWidth={2} />
                        </div>
                        <div className="insight-content">
                          <div className="insight-label">Active Patients</div>
                          <div className="insight-value">{insights.active}</div>
                        </div>
                      </div>
                      <div className="insight-card">
                        <div className="insight-icon insight-icon-new">
                          <TrendingUp size={20} strokeWidth={2} />
                        </div>
                        <div className="insight-content">
                          <div className="insight-label">New This Month</div>
                          <div className="insight-value">{insights.new}</div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Patients List */}
                  <section className="patients-list-section">
                    <div className="patients-section-header">
                      <h2 className="patients-card-title">Patients</h2>
                      <div className="patients-header-actions">
                        <div className="filter-group">
                          <label htmlFor="name-filter" className="filter-label">FILTER BY NAME</label>
                          <input
                            id="name-filter"
                            type="text"
                            className="filter-input"
                            placeholder="Search by name or email..."
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                          />
                        </div>
                        <button
                          type="button"
                          className="create-patient-btn"
                          onClick={() => navigate('/patients/create')}
                        >
                          <Check size={18} strokeWidth={2} aria-hidden />
                          Create New Patient
                        </button>
                      </div>
                    </div>

                    <div className="patients-list">
                      {filteredPatients.length === 0 ? (
                        <div className="no-patients">
                          {patients.length === 0
                            ? 'No patients found'
                            : 'No patients match your filters'}
                        </div>
                      ) : (
                        filteredPatients.map((patient) => (
                          <div
                            key={patient.id}
                            className="patient-card"
                          >
                            <div className="patient-avatar">
                              <span>{getInitials(patient.name)}</span>
                            </div>
                            <div className="patient-info">
                              <div
                                className="patient-name"
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate(`/patients/${patient.id}`)}
                              >
                                {patient.name}
                              </div>
                              {patient.email && (
                                <div className="patient-email">{patient.email}</div>
                              )}
                              <div className="patient-details-line">
                                <span className="stat-label">Verification:</span>
                                <span className="stat-value">
                                  {patient.verificationStatus === 'completed' ? 'Completed' : 'Incomplete'}
                                </span>
                                <span className="stat-sep"> · </span>
                                <span className="stat-label">Appointments:</span>
                                <span className="stat-value">{patient.totalAppointments}</span>
                                <span className="stat-sep"> · </span>
                                <span className="stat-label">Last Visit:</span>
                                <span className="stat-value">{formatDate(patient.lastAppointmentDate)}</span>
                              </div>
                            </div>
                            <div
                              className="patient-actions"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                className="details-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/patients/${patient.id}`);
                                }}
                              >
                                Details
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </section>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default PatientsPage;
